import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  Badge,
  Modal,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  DatePicker,
  Alert,
  Cards,
  Grid,
  ColumnLayout,
  StatusIndicator,
  Tabs,
  Toggle,
  Multiselect
} from '@cloudscape-design/components';

const API_BASE_URL = 'http://localhost:3000/api';

const TaskManager = ({ 
  familyMembers = [], 
  currentUser,
  onCreateTask,
  onUpdateTask,
  onDeleteTask 
}) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // 加载任务数据
  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/tasks`, {
        params: {
          family_id: currentUser?.family_id || '6869d0771dfa97eaefc3bbf7',
          date: today
        }
      });
      
      // 检查API返回的数据是否为空
      if (!response.data || response.data.length === 0) {
        setTasks([]);
        return;
      }
      
      setTasks(response.data);
    } catch (error) {
      console.error('❌ 加载任务失败:', error);
      setError('获取任务失败，请检查网络连接或稍后重试');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载任务
  useEffect(() => {
    if (currentUser) {
      loadTasks();
    }
  }, [currentUser]);

  // 新任务表单状态
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignee_id: '',
    due_date: '',
    priority: 'medium',
    points: 10,
    category: 'household',
    recurring: false,
    recurring_type: 'daily'
  });

  // 批量操作状态
  const [batchOperation, setBatchOperation] = useState({
    type: 'assign',
    assignee_id: '',
    priority: 'medium',
    due_date: '',
    points: 10
  });

  // 重置新任务表单
  const resetNewTaskForm = () => {
    setNewTask({
      title: '',
      description: '',
      assignee_id: '',
      due_date: '',
      priority: 'medium',
      points: 10,
      category: 'household',
      recurring: false,
      recurring_type: 'daily'
    });
  };

  // 处理创建任务
  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.assignee_id) {
      setError('请填写任务标题和指定执行人');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onCreateTask(newTask);
      setShowCreateModal(false);
      resetNewTaskForm();
    } catch (error) {
      setError('创建任务失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 处理批量操作
  const handleBatchOperation = async () => {
    if (selectedTasks.length === 0) {
      setError('请选择要操作的任务');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updates = {};
      if (batchOperation.type === 'assign' && batchOperation.assignee_id) {
        updates.assignee_id = batchOperation.assignee_id;
      }
      if (batchOperation.type === 'priority') {
        updates.priority = batchOperation.priority;
      }
      if (batchOperation.type === 'due_date' && batchOperation.due_date) {
        updates.due_date = batchOperation.due_date;
      }
      if (batchOperation.type === 'points') {
        updates.points = batchOperation.points;
      }

      // 批量更新任务
      for (const taskId of selectedTasks) {
        await onUpdateTask(taskId, updates);
      }

      setShowBatchModal(false);
      setSelectedTasks([]);
    } catch (error) {
      setError('批量操作失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 处理删除选中任务
  const handleDeleteSelected = async () => {
    if (selectedTasks.length === 0) {
      setError('请选择要删除的任务');
      return;
    }

    if (window.confirm(`确定要删除选中的 ${selectedTasks.length} 个任务吗？`)) {
      try {
        setLoading(true);
        for (const taskId of selectedTasks) {
          await onDeleteTask(taskId);
        }
        setSelectedTasks([]);
      } catch (error) {
        setError('删除任务失败：' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // 获取执行人姓名
  const getAssigneeName = (assigneeId) => {
    const member = familyMembers.find(m => m._id === assigneeId);
    return member ? member.name : '未知用户';
  };

  // 获取状态徽章
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { color: 'blue', text: '待完成' },
      in_progress: { color: 'grey', text: '进行中' },
      completed: { color: 'green', text: '已完成' },
      overdue: { color: 'red', text: '已逾期' }
    };
    const statusInfo = statusMap[status] || statusMap.pending;
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  // 获取优先级徽章
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      low: { color: 'green', text: '低' },
      medium: { color: 'blue', text: '中' },
      high: { color: 'red', text: '高' }
    };
    const priorityInfo = priorityMap[priority] || priorityMap.medium;
    return <Badge color={priorityInfo.color}>{priorityInfo.text}</Badge>;
  };

  // 根据标签页过滤任务
  const getFilteredTasks = () => {
    switch (activeTab) {
      case 'pending':
        return tasks.filter(t => t.status === 'pending');
      case 'in_progress':
        return tasks.filter(t => t.status === 'in_progress');
      case 'completed':
        return tasks.filter(t => t.status === 'completed');
      case 'overdue':
        return tasks.filter(t => t.status === 'overdue');
      case 'my_tasks':
        return tasks.filter(t => t.assignee_id === currentUser?._id);
      default:
        return tasks;
    }
  };

  const filteredTasks = getFilteredTasks();

  // 表格列定义
  const columnDefinitions = [
    {
      id: 'selection',
      header: '',
      cell: item => (
        <input
          type="checkbox"
          checked={selectedTasks.includes(item._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedTasks(prev => [...prev, item._id]);
            } else {
              setSelectedTasks(prev => prev.filter(id => id !== item._id));
            }
          }}
        />
      ),
      width: 50
    },
    {
      id: 'title',
      header: '任务标题',
      cell: item => (
        <Box>
          <Box fontWeight="bold">{item.title}</Box>
          {item.description && (
            <Box fontSize="body-s" color="text-body-secondary">
              {item.description.substring(0, 50)}
              {item.description.length > 50 ? '...' : ''}
            </Box>
          )}
        </Box>
      ),
      sortingField: 'title'
    },
    {
      id: 'assignee',
      header: '执行人',
      cell: item => getAssigneeName(item.assignee_id)
    },
    {
      id: 'status',
      header: '状态',
      cell: item => getStatusBadge(item.status)
    },
    {
      id: 'priority',
      header: '优先级',
      cell: item => getPriorityBadge(item.priority)
    },
    {
      id: 'category',
      header: '分类',
      cell: item => (
        <Badge color="grey">
          {getCategoryName(item.category)}
        </Badge>
      )
    },
    {
      id: 'points',
      header: '金币',
      cell: item => (
        <Box display="flex" alignItems="center">
          <span style={{ color: '#ff9900', marginRight: '4px' }}>🪙</span>
          {item.points || 0}
        </Box>
      )
    },
    {
      id: 'due_date',
      header: '截止日期',
      cell: item => item.due_date ? new Date(item.due_date).toLocaleDateString() : '-'
    },
    {
      id: 'created_at',
      header: '创建时间',
      cell: item => item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'
    }
  ];

  // 获取分类名称
  const getCategoryName = (category) => {
    const categoryMap = {
      household: '家务',
      study: '学习',
      work: '工作',
      health: '健康',
      other: '其他'
    };
    return categoryMap[category] || '其他';
  };

  // 优先级选项
  const priorityOptions = [
    { label: '低', value: 'low' },
    { label: '中', value: 'medium' },
    { label: '高', value: 'high' }
  ];

  // 分类选项
  const categoryOptions = [
    { label: '家务', value: 'household' },
    { label: '学习', value: 'study' },
    { label: '工作', value: 'work' },
    { label: '健康', value: 'health' },
    { label: '其他', value: 'other' }
  ];

  // 重复类型选项
  const recurringOptions = [
    { label: '每日', value: 'daily' },
    { label: '每周', value: 'weekly' },
    { label: '每月', value: 'monthly' }
  ];

  // 批量操作选项
  const batchOperationOptions = [
    { label: '分配执行人', value: 'assign' },
    { label: '设置优先级', value: 'priority' },
    { label: '设置截止日期', value: 'due_date' },
    { label: '设置奖励金币', value: 'points' }
  ];

  // 标签页配置
  const tabs = [
    { label: '全部任务', id: 'all' },
    { label: '待完成', id: 'pending' },
    { label: '进行中', id: 'in_progress' },
    { label: '已完成', id: 'completed' },
    { label: '已逾期', id: 'overdue' },
    { label: '我的任务', id: 'my_tasks' }
  ];

  return (
    <SpaceBetween size="l">
      {/* 错误提示 */}
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 主要内容 */}
      <Container
        header={
          <Header
            variant="h2"
            description="创建、编辑和管理家庭任务"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => setShowBatchModal(true)}
                  disabled={selectedTasks.length === 0}
                >
                  批量操作 ({selectedTasks.length})
                </Button>
                <Button
                  onClick={handleDeleteSelected}
                  disabled={selectedTasks.length === 0}
                  loading={loading}
                >
                  删除选中
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  创建新任务
                </Button>
              </SpaceBetween>
            }
          >
            任务管理
          </Header>
        }
      >
        <SpaceBetween size="l">
          {/* 标签页 */}
          <Tabs
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            tabs={tabs.map(tab => ({
              label: tab.label,
              id: tab.id,
              content: null
            }))}
          />

          {/* 任务统计 */}
          <Grid gridDefinition={[
            { colspan: { default: 12, xs: 6, s: 2 } },
            { colspan: { default: 12, xs: 6, s: 2 } },
            { colspan: { default: 12, xs: 6, s: 2 } },
            { colspan: { default: 12, xs: 6, s: 2 } },
            { colspan: { default: 12, xs: 6, s: 2 } },
            { colspan: { default: 12, xs: 6, s: 2 } }
          ]}>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-info">
                  {tasks.length}
                </Box>
                <Box fontSize="body-s">总任务</Box>
              </Box>
            </Container>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-warning">
                  {tasks.filter(t => t.status === 'pending').length}
                </Box>
                <Box fontSize="body-s">待完成</Box>
              </Box>
            </Container>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-info">
                  {tasks.filter(t => t.status === 'in_progress').length}
                </Box>
                <Box fontSize="body-s">进行中</Box>
              </Box>
            </Container>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
                  {tasks.filter(t => t.status === 'completed').length}
                </Box>
                <Box fontSize="body-s">已完成</Box>
              </Box>
            </Container>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {tasks.filter(t => t.status === 'overdue').length}
                </Box>
                <Box fontSize="body-s">已逾期</Box>
              </Box>
            </Container>
            <Container>
              <Box textAlign="center">
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-info">
                  {currentUser ? tasks.filter(t => t.assignee_id === currentUser._id).length : 0}
                </Box>
                <Box fontSize="body-s">我的任务</Box>
              </Box>
            </Container>
          </Grid>

          {/* 任务表格 */}
          <Table
            columnDefinitions={columnDefinitions}
            items={filteredTasks}
            loadingText="加载任务中..."
            loading={loading}
            empty={
              <Box textAlign="center" color="inherit">
                <b>没有找到任务</b>
                <Box variant="p" color="inherit">
                  {tasks.length === 0 ? '还没有创建任何任务' : '当前分类下没有任务'}
                </Box>
              </Box>
            }
            sortingDisabled={false}
            variant="borderless"
            selectionType="multi"
          />

          {/* 空任务列表提示 */}
          {tasks.length === 0 && !loading && (
            <Alert type="info" header="没有任务">
              当前没有任务，点击"创建新任务"按钮开始添加任务。
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {/* 创建任务模态框 */}
      <Modal
        onDismiss={() => {
          setShowCreateModal(false);
          resetNewTaskForm();
          setError(null);
        }}
        visible={showCreateModal}
        closeAriaLabel="关闭模态框"
        size="large"
        header="创建新任务"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowCreateModal(false);
                  resetNewTaskForm();
                  setError(null);
                }}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateTask}
                loading={loading}
              >
                创建任务
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="任务标题" stretch>
              <Input
                value={newTask.title}
                onChange={({ detail }) => setNewTask(prev => ({ ...prev, title: detail.value }))}
                placeholder="输入任务标题"
              />
            </FormField>

            <FormField label="任务描述" stretch>
              <Textarea
                value={newTask.description}
                onChange={({ detail }) => setNewTask(prev => ({ ...prev, description: detail.value }))}
                placeholder="输入任务描述（可选）"
                rows={3}
              />
            </FormField>

            <ColumnLayout columns={2}>
              <FormField label="执行人">
                <Select
                  selectedOption={
                    newTask.assignee_id 
                      ? { label: getAssigneeName(newTask.assignee_id), value: newTask.assignee_id }
                      : null
                  }
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, assignee_id: detail.selectedOption.value }))}
                  options={familyMembers.map(member => ({
                    label: member.name,
                    value: member._id
                  }))}
                  placeholder="选择执行人"
                />
              </FormField>

              <FormField label="截止日期">
                <DatePicker
                  value={newTask.due_date}
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, due_date: detail.value }))}
                  placeholder="选择截止日期"
                />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={3}>
              <FormField label="优先级">
                <Select
                  selectedOption={{ label: priorityOptions.find(o => o.value === newTask.priority)?.label, value: newTask.priority }}
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, priority: detail.selectedOption.value }))}
                  options={priorityOptions}
                />
              </FormField>

              <FormField label="分类">
                <Select
                  selectedOption={{ label: categoryOptions.find(o => o.value === newTask.category)?.label, value: newTask.category }}
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, category: detail.selectedOption.value }))}
                  options={categoryOptions}
                />
              </FormField>

              <FormField label="奖励金币">
                <Input
                  type="number"
                  value={newTask.points.toString()}
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, points: parseInt(detail.value) || 0 }))}
                  placeholder="10"
                />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={2}>
              <FormField label="重复任务">
                <Toggle
                  checked={newTask.recurring}
                  onChange={({ detail }) => setNewTask(prev => ({ ...prev, recurring: detail.checked }))}
                >
                  启用重复
                </Toggle>
              </FormField>

              {newTask.recurring && (
                <FormField label="重复频率">
                  <Select
                    selectedOption={{ label: recurringOptions.find(o => o.value === newTask.recurring_type)?.label, value: newTask.recurring_type }}
                    onChange={({ detail }) => setNewTask(prev => ({ ...prev, recurring_type: detail.selectedOption.value }))}
                    options={recurringOptions}
                  />
                </FormField>
              )}
            </ColumnLayout>
          </SpaceBetween>
        </Form>
      </Modal>

      {/* 批量操作模态框 */}
      <Modal
        onDismiss={() => setShowBatchModal(false)}
        visible={showBatchModal}
        closeAriaLabel="关闭模态框"
        size="medium"
        header={`批量操作 (${selectedTasks.length} 个任务)`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => setShowBatchModal(false)}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={handleBatchOperation}
                loading={loading}
              >
                执行操作
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="操作类型">
              <Select
                selectedOption={{ label: batchOperationOptions.find(o => o.value === batchOperation.type)?.label, value: batchOperation.type }}
                onChange={({ detail }) => setBatchOperation(prev => ({ ...prev, type: detail.selectedOption.value }))}
                options={batchOperationOptions}
              />
            </FormField>

            {batchOperation.type === 'assign' && (
              <FormField label="新执行人">
                <Select
                  selectedOption={
                    batchOperation.assignee_id 
                      ? { label: getAssigneeName(batchOperation.assignee_id), value: batchOperation.assignee_id }
                      : null
                  }
                  onChange={({ detail }) => setBatchOperation(prev => ({ ...prev, assignee_id: detail.selectedOption.value }))}
                  options={familyMembers.map(member => ({
                    label: member.name,
                    value: member._id
                  }))}
                  placeholder="选择执行人"
                />
              </FormField>
            )}

            {batchOperation.type === 'priority' && (
              <FormField label="新优先级">
                <Select
                  selectedOption={{ label: priorityOptions.find(o => o.value === batchOperation.priority)?.label, value: batchOperation.priority }}
                  onChange={({ detail }) => setBatchOperation(prev => ({ ...prev, priority: detail.selectedOption.value }))}
                  options={priorityOptions}
                />
              </FormField>
            )}

            {batchOperation.type === 'due_date' && (
              <FormField label="新截止日期">
                <DatePicker
                  value={batchOperation.due_date}
                  onChange={({ detail }) => setBatchOperation(prev => ({ ...prev, due_date: detail.value }))}
                  placeholder="选择截止日期"
                />
              </FormField>
            )}

            {batchOperation.type === 'points' && (
              <FormField label="新奖励金币">
                <Input
                  type="number"
                  value={batchOperation.points.toString()}
                  onChange={({ detail }) => setBatchOperation(prev => ({ ...prev, points: parseInt(detail.value) || 0 }))}
                  placeholder="10"
                />
              </FormField>
            )}
          </SpaceBetween>
        </Form>
      </Modal>
    </SpaceBetween>
  );
};

export default TaskManager;